import { createContext, useCallback, useContext, useRef, useState } from "react";
import Dialog from "@/components/ui/Dialog";
import PicklistSelect from "@/components/PicklistSelect";

/**
 * "Why was it lost?" — moving a lead or quotation into a lost stage needs a reason from the Master Data
 * "Lost reasons" list (the server answers 400 `lost_reason_required` without one). Wrap the save:
 *
 *   const withWhyLost = useWhyLost();
 *   const res = await withWhyLost((extra) => api.put(`/leads/${id}`, { ...payload, ...extra }));
 *   if (!res) return;            // the person closed the question: nothing was saved
 *
 * The first try goes as it is; if the server asks why, the question pops up and the save is sent again
 * with `{lost_reason, lost_note}`. Any other error is thrown as usual.
 */
const Ctx = createContext(null);

export const isLostReasonError = (e) => e?.response?.data?.detail?.code === "lost_reason_required";

export function LostReasonProvider({ children }) {
  const [ask, setAsk] = useState(null);           // {stage, resolve}
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const pending = useRef(null);

  const question = useCallback((stage) => new Promise((resolve) => {
    pending.current = resolve;
    setReason(""); setNote("");
    setAsk({ stage });
  }), []);

  const finish = (answer) => {
    const resolve = pending.current;
    pending.current = null;
    setAsk(null);
    resolve?.(answer);
  };

  const withWhyLost = useCallback(async (save) => {
    try {
      return await save({});
    } catch (e) {
      if (!isLostReasonError(e)) throw e;
      const why = await question(e.response.data.detail.stage || "Lost");
      if (!why) return null;
      return await save(why);
    }
  }, [question]);

  return (
    <Ctx.Provider value={withWhyLost}>
      {children}
      {ask && (
        <Dialog title="Why was it lost?" onClose={() => finish(null)} size="sm"
                footer={<>
                  <button type="button" className="btn-ghost" onClick={() => finish(null)}>Cancel</button>
                  <button type="button" className="btn-primary" disabled={!reason} data-testid="lost-reason-save"
                          onClick={() => finish({ lost_reason: reason, lost_note: note.trim() })}>
                    Mark as {ask.stage}
                  </button>
                </>}>
          <form onSubmit={(e) => { e.preventDefault(); if (reason) finish({ lost_reason: reason, lost_note: note.trim() }); }}
                className="space-y-3">
            <p className="text-sm text-[var(--color-text-muted)]">
              Pick the main reason. It shows in Analytics → Leads and the Sales Tracker, so the team can see why deals slip.
            </p>
            <label className="block text-sm font-medium">
              Reason
              <PicklistSelect list="lost_reasons" value={reason} onChange={setReason} required placeholder="Choose a reason"
                              testId="lost-reason" ariaLabel="Lost reason"
                              className="mt-1 w-full min-h-[44px] px-3 rounded border border-[var(--color-border)] bg-[var(--color-surface)]" />
            </label>
            <label className="block text-sm font-medium">
              Note <span className="font-normal text-[var(--color-text-muted)]">(optional)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3}
                        placeholder="e.g. went with a cheaper local supplier" data-testid="lost-note"
                        className="mt-1 w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-surface)]" />
            </label>
          </form>
        </Dialog>
      )}
    </Ctx.Provider>
  );
}

/** -> withWhyLost(save). Outside the provider it just runs the save. */
export function useWhyLost() {
  return useContext(Ctx) || ((save) => save({}));
}
